// The game's sound effects on the phone, through Web Audio so a tap sounds at
// once and overlapping ticks do not cut each other off. A phone lets a page
// make sound only after a touch, so the first touch anywhere unlocks it
// (`unlockOnFirstTouch`). The viewer can switch sound off; the choice is kept.

import { readStore, writeStore } from './dom';

export type Sound = 'tick' | 'click' | 'ding' | 'bump' | 'pop';

// The game picks one of seven ticks at random for each counter step.
const TICK_FILES = ['tick1', 'tick2', 'tick3', 'tick4', 'tick5', 'tick6', 'tick7'];
const FILES: Record<Exclude<Sound, 'tick'>, string> = {
  click: 'button-click',
  ding: 'glass-ding',
  bump: 'drop1',
  pop: 'pop',
};
// Levels match the game's mix closely enough on a phone speaker.
const VOLUME: Record<Sound, number> = { tick: 0.55, click: 0.5, ding: 0.7, bump: 0.6, pop: 0.6 };
const MUTE_KEY = 'llamallink.muted';

let context: AudioContext | null = null;
let muted = readStore(MUTE_KEY) === '1';
const buffers = new Map<string, AudioBuffer>();

function load(name: string): void {
  if (!context || buffers.has(name)) return;
  const audio = context;
  void fetch(`/sfx/${name}.mp3`)
    .then((response) => response.arrayBuffer())
    .then((data) => audio.decodeAudioData(data))
    .then((buffer) => buffers.set(name, buffer))
    .catch(() => {
      // A sound that fails to load stays silent; the page works without it.
    });
}

function unlock(): void {
  if (!context) {
    const Made =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Made) return;
    context = new Made();
    for (const name of [...TICK_FILES, ...Object.values(FILES)]) load(name);
  }
  if (context.state === 'suspended') void context.resume();
}

export function unlockOnFirstTouch(): void {
  const once = (): void => unlock();
  window.addEventListener('pointerdown', once, { passive: true });
  window.addEventListener('keydown', once);
}

export const isMuted = (): boolean => muted;

export function setMuted(on: boolean): void {
  muted = on;
  writeStore(MUTE_KEY, on ? '1' : '0');
}

// Plays `sound`, `pitch` 1 for as recorded. Silent until the first touch, while
// muted, or while the file is still loading.
export function play(sound: Sound, pitch = 1): void {
  if (muted || !context) return;
  const name =
    sound === 'tick'
      ? (TICK_FILES[Math.floor(Math.random() * TICK_FILES.length)] as string)
      : FILES[sound];
  const buffer = buffers.get(name);
  if (!buffer) return;
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.playbackRate.value = pitch;
  const gain = context.createGain();
  gain.gain.value = VOLUME[sound];
  source.connect(gain).connect(context.destination);
  source.start();
}
